/**
 * A CODE question: write real JavaScript, run it against the question's tests.
 *
 * **Monaco is loaded lazily, and that is not an optimisation.** It is roughly
 * a megabyte, and the students this is for are on connections where the exam
 * already fights to upload its own recording — the whole of the last week of
 * work was about not saturating that link. A student sitting a multiple-choice
 * paper must never pay for an editor they will not open, so the import happens
 * when a CODE question is first shown and not before.
 *
 * A plain textarea stands in until it arrives, and stays if it never does. On
 * a bad connection that is the difference between "no syntax highlighting" and
 * "cannot answer the question".
 *
 * **Running is deliberately explicit.** Tests run when the student presses
 * Run, never on a keystroke: an editor that executes as you type runs a
 * half-written loop on almost every character, and on a coding paper that is
 * how you hang someone's exam.
 */
import {
  Component,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Check, Loader2, Play, X } from "lucide-react";
import type { CodeTest, CodeTestResult } from "@exam/shared";
import { Button } from "@/components/ui";
import { cn } from "@/lib/cn";
import { runCodeTests } from "@/lib/code/runTests";

/**
 * Monaco, lazily, and served from THIS origin.
 *
 * `@monaco-editor/react` fetches Monaco from a CDN by default. That would put
 * a third-party request in the middle of a proctored exam, on connections that
 * already struggle to upload the recording — and if jsdelivr is slow, blocked
 * or simply unreachable from a school network, the editor never arrives. The
 * loader is pointed at the copy bundled with this app instead, so it comes
 * from the same place as the rest of the page and is cached with it.
 *
 * Still lazy: a multiple-choice paper never downloads any of it.
 */
const MonacoEditor = lazy(async () => {
  const [{ default: Editor, loader }, monaco] = await Promise.all([
    import("@monaco-editor/react"),
    // The EDITOR ONLY — not the `monaco-editor` barrel.
    //
    // The barrel pulls every language service Monaco ships: TypeScript, CSS,
    // HTML and JSON, each with its own web worker. That is about nine
    // megabytes for a JavaScript question, on the connections this whole app
    // is built around, to provide IntelliSense that is deliberately switched
    // off below anyway. The core editor plus one syntax definition is under a
    // megabyte and does everything a student needs: highlighting, brackets,
    // selection, undo.
    import("monaco-editor/editor/editor.api"),
  ]);

  // Monarch highlighting only. This is every "basic" language's tokenizer in
  // one file — the build ships them together — and it is still a fraction of
  // what the language SERVICES cost: those are the TypeScript, CSS, HTML and
  // JSON workers, about nine megabytes, providing IntelliSense that is
  // switched off below anyway.
  await import("monaco-editor/basic-languages/monaco.contribution");

  loader.config({ monaco });
  await loader.init();
  return { default: Editor };
});

export interface CodeAnswerValue {
  source: string;
  passed: number;
  total: number;
  results: CodeTestResult[];
}

export function CodeAnswer({
  questionId,
  starterCode,
  tests,
  value,
  onChange,
}: {
  questionId: string;
  starterCode: string | null;
  /** The VISIBLE tests. Hidden ones are never sent to the browser. */
  tests: CodeTest[];
  value: CodeAnswerValue | null;
  onChange: (value: CodeAnswerValue) => void;
}) {
  const [source, setSource] = useState(value?.source ?? starterCode ?? "");
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<CodeTestResult[]>(value?.results ?? []);
  const [error, setError] = useState<string | null>(null);
  const [editorFailed, setEditorFailed] = useState(false);

  // Switching questions must reset the editor to THAT question's state, not
  // carry the previous one's code across — which is what a naive
  // `useState(initial)` would do, since the initialiser only runs once.
  const shownFor = useRef(questionId);
  useEffect(() => {
    if (shownFor.current === questionId) return;
    shownFor.current = questionId;
    setSource(value?.source ?? starterCode ?? "");
    setResults(value?.results ?? []);
    setError(null);
  }, [questionId, value, starterCode]);

  /**
   * The answer is saved on every edit, with the LAST run's counts.
   *
   * Saving only on Run would lose a student's work if they edited and
   * submitted without re-running, which is the most likely way to end an exam.
   * The counts going stale is the lesser problem and an honest one: they
   * describe the last run, and the UI says when the code has changed since.
   */
  const publish = useCallback(
    (nextSource: string, run?: { passed: number; total: number; results: CodeTestResult[] }) => {
      onChange({
        source: nextSource,
        passed: run?.passed ?? value?.passed ?? 0,
        total: run?.total ?? value?.total ?? tests.length,
        results: run?.results ?? results,
      });
    },
    [onChange, results, tests.length, value],
  );

  const edit = (next: string) => {
    setSource(next);
    publish(next);
  };

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      const outcome = await runCodeTests(source, tests);
      setResults(outcome.results);
      setError(outcome.error);
      publish(source, {
        passed: outcome.passed,
        total: outcome.total,
        results: outcome.results,
      });
    } finally {
      setRunning(false);
    }
  };

  const ranSource = value?.source;
  const stale = results.length > 0 && ranSource !== undefined && ranSource !== source;

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-hidden rounded-md border border-border-strong">
        {editorFailed ? (
          <PlainEditor value={source} onChange={edit} />
        ) : (
          <Suspense fallback={<PlainEditor value={source} onChange={edit} loading />}>
            <MonacoBoundary onFail={() => setEditorFailed(true)}>
              <MonacoEditor
                height="320px"
                defaultLanguage="javascript"
                theme="vs"
                value={source}
                onChange={(next) => edit(next ?? "")}
                options={{
                  minimap: { enabled: false },
                  fontSize: 13,
                  lineNumbers: "on",
                  scrollBeyondLastLine: false,
                  tabSize: 2,
                  automaticLayout: true,
                  // Nothing that phones home or guesses: an exam is not the
                  // place for a model-backed suggestion.
                  quickSuggestions: false,
                  wordBasedSuggestions: "off",
                }}
              />
            </MonacoBoundary>
          </Suspense>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          icon={running ? <Loader2 className="animate-spin" /> : <Play />}
          disabled={running}
          onClick={() => void run()}
        >
          {running ? "Running…" : "Run tests"}
        </Button>

        {results.length > 0 ? (
          <span className="text-xs text-ink-muted">
            <span
              className={cn(
                "tabular font-medium",
                results.every((r) => r.passed) ? "text-answered" : "text-violation",
              )}
            >
              {results.filter((r) => r.passed).length}
            </span>{" "}
            of <span className="tabular">{results.length}</span> passing
          </span>
        ) : null}

        {stale ? (
          // Said out loud, because the saved marks come from the last run: a
          // student who edits and submits without re-running is graded on the
          // older result, and they should know that before they do it.
          <span className="text-2xs text-warn">
            You have edited the code since the last run — run it again.
          </span>
        ) : null}
      </div>

      {error ? (
        <p className="rounded-md border border-violation/40 bg-violation-soft px-3 py-2 font-mono text-2xs text-violation">
          {error}
        </p>
      ) : null}

      {tests.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {tests.map((test) => {
            const result = results.find((r) => r.name === test.name);
            return (
              <li
                key={test.name}
                className={cn(
                  "flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-2xs",
                  result === undefined
                    ? "border-border bg-surface-sunken text-ink-muted"
                    : result.passed
                      ? "border-answered/30 bg-answered-soft text-ink"
                      : "border-violation/30 bg-violation-soft text-ink",
                )}
              >
                {result === undefined ? (
                  <span className="mt-0.5 size-3.5 shrink-0 rounded-full border border-border-strong" />
                ) : result.passed ? (
                  <Check className="mt-0.5 size-3.5 shrink-0 text-answered" />
                ) : (
                  <X className="mt-0.5 size-3.5 shrink-0 text-violation" />
                )}
                <span className="min-w-0">
                  <span className="block font-medium">{test.name}</span>
                  {result?.detail ? (
                    <span className="block font-mono text-ink-muted">
                      {result.detail}
                    </span>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-2xs text-ink-faint">
          This question has no automatic checks — your code is saved and marked
          by hand.
        </p>
      )}
    </div>
  );
}

/**
 * The fallback editor, and the one that is used if Monaco never arrives.
 *
 * A textarea with a monospace font answers the question perfectly well. The
 * exam must not depend on a megabyte of editor downloading successfully.
 */
function PlainEditor({
  value,
  onChange,
  loading = false,
}: {
  value: string;
  onChange: (next: string) => void;
  loading?: boolean;
}) {
  return (
    <div className="relative">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        rows={14}
        className="w-full resize-y bg-surface p-3 font-mono text-xs text-ink outline-none"
      />
      {loading ? (
        <span className="absolute top-2 right-2 flex items-center gap-1 rounded-sm bg-surface-sunken px-1.5 py-0.5 text-2xs text-ink-faint">
          <Loader2 className="size-3 animate-spin" /> editor loading
        </span>
      ) : null}
    </div>
  );
}

/**
 * Catches a failed Monaco load so the question stays answerable.
 *
 * Without this, a chunk that 404s (a stale cached bundle, a dropped
 * connection) throws during render and takes the whole exam page down — losing
 * an attempt over a syntax-highlighting library.
 */
class MonacoBoundary extends Component<
  { children: ReactNode; onFail: () => void },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch() {
    this.props.onFail();
  }

  override render() {
    return this.state.failed ? null : this.props.children;
  }
}
